import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import { ChevronLeft, Calendar, Tag, Eye, ArrowUpRight } from "lucide-react";

// ── Defensive markdown cleanup ────────────────────────────────────────────
// AutoSEO occasionally ships markdown that has been concatenated without
// proper blank-line separation, with embedded `<a name="...">` anchors, or
// with escaped underscores inside URLs. Mirrors backend `_clean_markdown`
// so historical posts render correctly without a re-publish.
function cleanMarkdown(md) {
  if (!md) return "";
  let out = md;
  // 1. Strip `<a name="..."></a>` anchors glued to headings.
  out = out.replace(/<a\s+name=["'][^"']+["']\s*>\s*<\/a>/gi, "");
  // 2. Inject blank line before ## / ### / #### headings that share a line
  //    with body text. Negative lookbehind avoids re-splitting the heading's
  //    own `###` hash run into `# + ##`.
  out = out.replace(/(?<![\n#])(#{1,6}\s+)/g, "\n\n$1");
  // 3. Unescape `\_` inside URL parens (Sports\_biomechanics → Sports_biomechanics).
  for (let i = 0; i < 4 && /\(https?:\/\/[^)\s]*?\\_/.test(out); i++) {
    out = out.replace(/(\(https?:\/\/[^)\s]*?)\\_/g, "$1_");
  }
  // 4. Collapse runs of 3+ blank lines.
  out = out.replace(/\n{3,}/g, "\n\n");
  return out.trim();
}

function firstMarkdownImage(md) {
  if (!md) return null;
  const m = md.match(/!\[[^\]]*\]\((https?:\/\/[^)\s]+)\)/);
  return m ? m[1] : null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then(({ data }) => {
      setPost(data.post);
      if (data.post?.seo?.meta_title) document.title = data.post.seo.meta_title;
      const meta = document.querySelector("meta[name='description']");
      if (meta && data.post?.seo?.meta_description) meta.setAttribute("content", data.post.seo.meta_description);
    }).catch(() => setNotFound(true));
    return () => { document.title = "ArmIQ"; };
  }, [slug]);

  const cleaned = useMemo(() => cleanMarkdown(post?.markdown), [post?.markdown]);
  const cover = post?.cover_image || firstMarkdownImage(cleaned);

  // AutoSEO ships BOTH `html` and `markdown` for each article. The HTML is
  // perfectly clean (proper <h2>/<h3>/<p>/<a>); the markdown is mangled with
  // escaped brackets, run-on headings, and broken TOC anchor links. Prefer
  // HTML when present and substantial, fall back to cleaned markdown.
  const sanitizedHtml = useMemo(() => {
    if (!post?.html || post.html.length < 200) return null;
    return DOMPurify.sanitize(post.html, {
      ADD_ATTR: ["target", "rel"],
      FORBID_TAGS: ["script", "style", "iframe", "form", "input", "button"],
    });
  }, [post?.html]);

  // Reading time = ~225 wpm.
  const readingMin = useMemo(() => {
    const source = sanitizedHtml || cleaned || "";
    if (!source) return null;
    const text = source.replace(/<[^>]+>/g, " ");
    const words = text.split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 225));
  }, [sanitizedHtml, cleaned]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/60 text-sm" style={{ backgroundColor: "var(--ink-1)" }}>
        Post not found. <Link to="/blog" className="ml-2 text-cyan-300 underline">Back to blog</Link>
      </div>
    );
  }
  if (!post) return <div className="min-h-screen flex items-center justify-center text-white/40 text-sm" style={{ backgroundColor: "var(--ink-1)" }}>Loading…</div>;

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--ink-1)" }} data-testid="blog-post">
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/" dark />
          <Link to="/blog" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1" data-testid="blog-post-back">
            <ChevronLeft className="h-4 w-4" /> All articles
          </Link>
        </div>
      </header>

      {/* HERO */}
      <article className="max-w-3xl mx-auto px-6 pt-12">
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold mb-4" style={{ color: "var(--brand-secondary)" }}>
          {post.category || "ArmIQ Journal"}
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl tracking-tighter text-white leading-[1.04]">
          {post.title}
        </h1>
        {post.excerpt && (
          <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-2xl">{post.excerpt}</p>
        )}
        <div className="flex flex-wrap items-center gap-4 text-xs text-white/55 mt-6 pb-6 border-b border-white/10">
          {post.author && <span className="font-bold text-white/80">{post.author}</span>}
          <span className="inline-flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {new Date(post.published_at).toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" })}</span>
          {readingMin && <span className="inline-flex items-center gap-1.5">· {readingMin} min read</span>}
          {post.view_count != null && <span className="inline-flex items-center gap-1.5"><Eye className="h-3 w-3" /> {post.view_count}</span>}
        </div>

        {cover && (
          <figure className="mt-8 rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
            <img src={cover} alt={post.title} className="w-full block aspect-[16/9] object-cover" loading="eager" data-testid="blog-post-cover" />
          </figure>
        )}

        {/* BODY — prefer AutoSEO's clean HTML over their broken markdown */}
        <div className="mt-10 blog-prose">
          {sanitizedHtml
            ? <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
            : cleaned
              ? <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={mdComponents}>{cleaned}</ReactMarkdown>
              : <div className="text-white/40 text-sm">No content.</div>}
        </div>

        {/* Inline gallery (any images in `images[]` not already shown inline) */}
        {Array.isArray(post.images) && post.images.length > 0 && (
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-3">
            {post.images.slice(0, 6).map((src) => (
              <img key={src} src={src} alt="" className="rounded-xl border border-white/10 aspect-square object-cover w-full" loading="lazy" />
            ))}
          </div>
        )}

        {/* FOOTER tags + CTA */}
        {(post.tags || []).length > 0 && (
          <div className="mt-12 pt-6 border-t border-white/10 flex flex-wrap items-center gap-2">
            <Tag className="h-3.5 w-3.5 text-white/40" />
            {post.tags.map((t) => (
              <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`}
                className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-white/5 text-white/70 border border-white/10 hover:bg-white/10">
                {t}
              </Link>
            ))}
          </div>
        )}

        <div className="mt-12 rounded-2xl p-6 sm:p-8 border" style={{ borderColor: "rgba(217,4,41,0.30)", background: "linear-gradient(180deg, rgba(217,4,41,0.10), rgba(217,4,41,0.02))" }}>
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold mb-2" style={{ color: "var(--brand-secondary)" }}>Built on the research</div>
          <div className="font-display font-black text-2xl text-white leading-tight">Bring ArmIQ to your pitching staff.</div>
          <p className="text-sm text-white/65 mt-2 max-w-xl">Closed-loop readiness, telemetry, and automated workload alerts — everything this article describes, deployable for your program in a week.</p>
          <Link to="/" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full text-white" style={{ backgroundColor: "var(--brand-secondary)" }}>
            See how it works <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </article>
      <Footer />
    </div>
  );
}

const mdComponents = {
  h1: (p) => <h1 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tighter mt-14 mb-5 leading-[1.1]" {...p} />,
  h2: (p) => (
    <h2 className="font-display font-black text-2xl sm:text-3xl text-white tracking-tight mt-14 mb-4 leading-tight pb-2 border-b border-white/10" {...p} />
  ),
  h3: (p) => (
    <h3 className="font-display font-bold text-xl sm:text-2xl text-white tracking-tight mt-10 mb-3 leading-snug" {...p} />
  ),
  h4: (p) => <h4 className="font-display font-bold text-lg text-white mt-8 mb-2" {...p} />,
  p:  ({ children, ...rest }) => {
    // react-markdown wraps an orphan-line image in a `<p>`. Our custom `img`
    // renderer emits a `<figure>`, which is invalid inside `<p>`. Detect a
    // paragraph whose only child is an image/figure and render the children
    // directly (no `<p>` wrapper) to keep the DOM tree valid.
    const arr = Array.isArray(children) ? children.filter((c) => c !== "\n" && c !== "") : [children];
    const onlyChild = arr.length === 1 ? arr[0] : null;
    const t = onlyChild?.type;
    const tag = typeof t === "string" ? t : t?.name || t?.displayName;
    if (tag === "img" || tag === "figure") return <>{children}</>;
    return <p className="text-[17px] text-white/80 leading-[1.75] mb-5" {...rest}>{children}</p>;
  },
  a:  ({ href, ...rest }) => (
    <a href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
       className="font-semibold underline underline-offset-4 decoration-[var(--brand-secondary)]/50 hover:decoration-[var(--brand-secondary)]"
       style={{ color: "var(--brand-secondary)" }} {...rest} />
  ),
  strong: (p) => <strong className="text-white font-bold" {...p} />,
  em: (p) => <em className="text-white/90 italic" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 text-white/80 my-6 space-y-2 marker:text-[var(--brand-secondary)]" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 text-white/80 my-6 space-y-2 marker:text-[var(--brand-secondary)] marker:font-bold" {...p} />,
  li: (p) => <li className="text-[17px] leading-[1.75] pl-1" {...p} />,
  blockquote: (p) => (
    <blockquote className="my-8 border-l-4 pl-5 py-1 italic text-white/75 text-lg leading-relaxed"
                style={{ borderColor: "var(--brand-secondary)" }} {...p} />
  ),
  hr: () => <hr className="my-10 border-white/10" />,
  code: ({ inline, ...rest }) => inline
    ? <code className="px-1.5 py-0.5 rounded bg-white/10 text-cyan-200 text-[0.9em] font-mono" {...rest} />
    : <code className="block p-4 rounded-lg bg-black/40 text-cyan-200 text-sm overflow-x-auto font-mono leading-relaxed" {...rest} />,
  pre: (p) => <pre className="my-6 rounded-lg overflow-hidden" {...p} />,
  img: ({ alt, src, ...rest }) => (
    <figure className="my-8">
      <img src={src} alt={alt || ""} className="rounded-xl border border-white/10 w-full" loading="lazy" {...rest} />
      {alt && <figcaption className="text-center text-xs text-white/40 mt-2 italic">{alt}</figcaption>}
    </figure>
  ),
  table: (p) => (
    <div className="my-8 overflow-x-auto rounded-lg border border-white/10">
      <table className="w-full text-sm text-white/80" {...p} />
    </div>
  ),
  thead: (p) => <thead className="bg-white/5 text-white/60 text-[11px] uppercase tracking-wide" {...p} />,
  th: (p) => <th className="px-4 py-2.5 text-left font-bold" {...p} />,
  td: (p) => <td className="px-4 py-2.5 border-t border-white/5" {...p} />,
  // Suppress orphan inline HTML anchors that AutoSEO sometimes leaves behind
  // (e.g. `<a name="key-takeaways"></a>`). rehype-raw lets these through, so
  // we silently render the children only.
  a_name: () => null,
};
